
'use client';

import React, { useEffect, useState } from 'react';
import { ComponentNode } from '../types/template';
import { convertStylesToInline, isResponsiveSidebar, isResponsiveSplitLayout } from '../utils/styleUtils';
import ComponentRenderer from './ComponentRenderer';
import { resolveCustomThemeHref } from '../utils/linkHelpers';
import { getNestedValue } from '../utils/dataBinding';
import { normalizeTemplateItem } from '../utils/normalizeTemplateItem';
import { fetchRawDataForRenderRequest } from '../utils/DataFetcher';
import { buildRequestConfig, getRouteTemplateVariables } from '../utils/templateVariables';
import { buildBreadcrumbCrumbs } from '../renderer';
import HoverGroup from './HoverGroup';

interface ContainerComponentProps {
    component: ComponentNode;
    data?: any;
    context?: {
        companyId?: string;
        templateId?: string;
        domain?: string;
        pageSlug?: string;
        basePath?: string;
        params?: Record<string, any>;
        store_slug?: string;
        slug_type?: string;
        category_slug?: string;
        promotion_slug?: string;
        out_link?: string;
        staticPageData?: any;
    };
}

const ContainerComponent: React.FC<ContainerComponentProps> = ({
    component,
    data,
    context,
}) => {
    const { styles, components, customClassName } = component;
    let { href } = component as any;
    const { target, rel, repeated, dataSourceKey } = component as any;
    const inlineStyles = convertStylesToInline(styles);
    const containerClassName = [
        customClassName,
        isResponsiveSplitLayout(component) ? 'cb-responsive-split-layout' : undefined,
        isResponsiveSidebar(component) ? 'cb-responsive-sidebar' : undefined,
    ].filter(Boolean).join(' ') || undefined;

    /**
     * A container can carry its own request.
     *
     * The section components each fetch their own data, so nothing fetched for
     * a plain container — and the header's nav dropdowns are containers with an
     * `apiConfig`. They rendered with their static children only: the panel
     * showed its "View all" link and none of the API rows, while the builder,
     * which fetches for any node declaring a request, filled them.
     */
    const ownRequest = (component as any).apiConfig ?? (component as any).apiEndpoint;
    const [fetched, setFetched] = useState<any>(null);

    useEffect(() => {
        if (!ownRequest) return;
        let cancelled = false;

        const load = async () => {
            try {
                const templateVars = getRouteTemplateVariables(context);
                const requestConfig = (component as any).apiConfig
                    ? buildRequestConfig((component as any).apiConfig, templateVars)
                    : { apiEndpoint: (component as any).apiEndpoint, method: 'POST' as const, body: {} };
                if (!requestConfig) return;

                const response = await fetchRawDataForRenderRequest(requestConfig, {
                    companyId: context?.companyId,
                    domain: context?.domain,
                });
                if (!cancelled) {
                    // Unwrap `response.data` for single-object APIs (e.g. get_footer_pages)
                    // so children can use placeholders like "page_name" directly.
                    // Skip unwrapping when descendants use "data.*" dataSourceKey paths
                    // (nav dropdowns, merchant grids) — those need the full response intact.
                    const hasDataPrefixedKey = (nodes?: ComponentNode[]): boolean => {
                        if (!nodes) return false;
                        return nodes.some(
                            (n: any) =>
                                (typeof n.dataSourceKey === 'string' && n.dataSourceKey.startsWith('data.')) ||
                                hasDataPrefixedKey(n.components),
                        );
                    };
                    const isSingleObject =
                        response?.data != null &&
                        !Array.isArray(response.data) &&
                        typeof response.data === 'object' &&
                        !hasDataPrefixedKey(components);
                    setFetched(isSingleObject ? response.data : response);
                }
            } catch (error) {
                console.error('ContainerComponent request failed:', error);
            }
        };

        void load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(ownRequest), context?.companyId, context?.domain]);

    // Children bind against whatever this container fetched, falling back to
    // the data handed down when it has no request of its own.
    const boundData = ownRequest ? fetched : data;

    /**
     * Route-derived breadcrumb trail: the crumb list isn't API data at all
     * (one template serves every category/page), so it's computed from
     * context.basePath/context.params here via the shared buildBreadcrumbCrumbs
     * (packages/template-renderer/resolve.ts, vendored into ../renderer),
     * instead of coming through the repeated/dataSourceKey path below. One
     * child (the LINK crumb template) is rendered per computed crumb — all
     * but the last get a real <Link>, matching the trail's previous (still
     * correct) production behavior exactly.
     */
    if ((component as any).breadcrumbCrumbs) {
        const crumbs = buildBreadcrumbCrumbs(context);
        const crumbTemplate = components?.[0];
        if (!crumbs || !crumbTemplate) return null;
        const crumbStyles = convertStylesToInline(crumbTemplate.styles);
        return (
            <div style={inlineStyles} className={customClassName}>
                {crumbs.map((crumb, index) => {
                    const isLast = index === crumbs.length - 1;
                    return (
                        <React.Fragment key={`${crumb.label}-${index}`}>
                            {index > 0 && (
                                <span style={{ margin: '0 6px' }} aria-hidden="true">
                                    &gt;
                                </span>
                            )}
                            {crumb.href && !isLast ? (
                                <a
                                    href={crumb.href}
                                    style={crumbStyles}
                                    className={(crumbTemplate as any).customClassName}
                                >
                                    {crumb.label}
                                </a>
                            ) : (
                                <span style={crumbStyles} className={(crumbTemplate as any).customClassName}>
                                    {crumb.label}
                                </span>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    }

    /**
     * `hoverGroup` splits the children into the always-visible trigger and the
     * panel revealed on hover. Declared in the JSON so no stylesheet is needed
     * to make the nav behave.
     */
    if ((component as any).hoverGroup) {
        const panelNode = components?.find((child: any) => child.hoverTarget);
        const triggers = components?.filter((child: any) => child !== panelNode);
        return (
            <HoverGroup
                style={inlineStyles}
                className={customClassName}
                trigger={triggers?.map((child) => (
                    <ComponentRenderer key={child.id} component={child} data={boundData} context={context} />
                ))}
                panel={
                    panelNode ? (
                        <ComponentRenderer component={panelNode} data={boundData} context={context} />
                    ) : null
                }
            />
        );
    }

    // Resolve href from data if it contains placeholder
    if (href) {
        href = resolveCustomThemeHref(href, boundData, context) || href;
    }

    const repeatedSource = (() => {
        if (Array.isArray(boundData)) {
            return boundData;
        }

        if (repeated && dataSourceKey) {
            const nestedSource = getNestedValue(boundData, dataSourceKey);
            if (Array.isArray(nestedSource)) {
                return nestedSource;
            }
        }

        return null;
    })();

    if (repeatedSource) {
        return (
            <>
                {repeatedSource.map((item: any, index: number) => {
                    const normalizedItem = normalizeTemplateItem(item);
                    const repeatedHref = href
                        ? resolveCustomThemeHref(href, normalizedItem, context) || href
                        : undefined;

                    const childrenContent = components?.map((child) => (
                        <ComponentRenderer key={`${child.id}-${index}`} component={child} data={normalizedItem} context={context} />
                    ));

                    if (repeatedHref && repeatedHref !== '#' && !repeatedHref.startsWith('[')) {
                        return (
                            <a
                                key={`${component.id}-${index}`}
                                href={repeatedHref}
                                target={target}
                                rel={rel}
                                style={inlineStyles}
                                className={customClassName}
                            >
                                {childrenContent}
                            </a>
                        );
                    }

                    return (
                        <div key={`${component.id}-${index}`} style={inlineStyles} className={customClassName}>
                            {childrenContent}
                        </div>
                    );
                })}
            </>
        );
    }

    const childrenContent = components?.map((child) => (
        <ComponentRenderer key={child.id} component={child} data={boundData} context={context} />
    ));

    if (href && href !== '#' && !href.startsWith('[')) {
        return (
            <a
                href={href}
                target={target}
                rel={rel}
                style={inlineStyles}
                className={containerClassName}
            >
                {childrenContent}
            </a>
        );
    }

    return (
        <div style={inlineStyles} className={containerClassName}>
            {childrenContent}
        </div>
    );
};

export default ContainerComponent;
